import React, {useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import {useIntl, FormattedMessage} from 'react-intl';
import {FlexColumn} from '@forecast-it/design-system';
import type {Message} from './useAIChatStream';
import {D, TRUNCATE_AT, PREVIEW_CHARS} from './design-tokens';
import {SparkleIcon, SmallBtn, ThinkingDots} from './icons';
import {StatusSteps} from './StatusSteps';

// ── Markdown styled components ─────────────────────────────────────────────────
// One named component per markdown element — zero inline styles in mdComponents.

const MdP = styled.p`
	margin: 0 0 8px;
	line-height: 1.65;
`;

const MdInternalLinkBtn = styled.button`
	background: none;
	border: none;
	padding: 0;
	color: ${D.accentMid};
	text-decoration: underline;
	cursor: pointer;
	font-family: inherit;
	font-size: inherit;
`;

const MdExternalLink = styled.a`
	color: ${D.accentMid};
`;

const MdInlineCode = styled.code`
	background: ${D.surface2};
	border: 1px solid ${D.border};
	border-radius: 3px;
	padding: 1px 5px;
	font-size: 0.88em;
	font-family: monospace;
	color: ${D.accent};
`;

const MdPre = styled.pre`
	background: ${D.surface};
	border: 1px solid ${D.border};
	border-radius: 6px;
	padding: 10px 12px;
	overflow-x: auto;
	font-size: 12px;
	line-height: 1.5;
	margin: 8px 0;
`;

const MdPreCode = styled.code`
	font-family: monospace;
	color: ${D.textPrimary};
`;

const MdUl = styled.ul`
	margin: 4px 0 8px;
	padding-left: 20px;
`;

const MdOl = styled.ol`
	margin: 4px 0 8px;
	padding-left: 20px;
`;

const MdLi = styled.li`
	margin-bottom: 2px;
	line-height: 1.6;
`;

const MdH1 = styled.h1`
	font-size: 15px;
	font-weight: 600;
	margin: 12px 0 6px;
	color: ${D.textPrimary};
`;

const MdH2 = styled.h2`
	font-size: 14px;
	font-weight: 600;
	margin: 10px 0 5px;
	color: ${D.textPrimary};
`;

const MdH3 = styled.h3`
	font-size: 13px;
	font-weight: 600;
	margin: 8px 0 4px;
	color: ${D.textPrimary};
`;

const MdTableWrapper = styled.div`
	overflow-x: auto;
	margin: 8px 0;
	border-radius: 6px;
	border: 1px solid ${D.border};
`;

const MdTable = styled.table`
	border-collapse: collapse;
	width: 100%;
	font-size: 12px;
`;

const MdTh = styled.th`
	padding: 6px 10px;
	background: ${D.surface};
	color: ${D.textSecondary};
	text-align: left;
	border-bottom: 1px solid ${D.border};
	font-weight: 600;
	font-size: 11px;
	letter-spacing: 0.3px;
`;

const MdTd = styled.td`
	padding: 6px 10px;
	border-bottom: 1px solid ${D.border};
	color: ${D.textPrimary};
	font-size: 12px;
`;

const MdBlockquote = styled.blockquote`
	border-left: 3px solid ${D.accentMid};
	margin: 8px 0;
	padding-left: 12px;
	color: ${D.textSecondary};
	font-style: italic;
`;

// ── Message styled components ──────────────────────────────────────────────────

const MessageRow = styled.div<{isUser: boolean}>`
	display: flex;
	flex-direction: ${({isUser}) => (isUser ? 'row-reverse' : 'row')};
	align-items: flex-start;
	gap: 8px;
	margin-bottom: 14px;
	animation: ai-fade 0.15s ease-out;
`;

const Avatar = styled.div<{isError: boolean}>`
	width: 26px;
	height: 26px;
	border-radius: 8px;
	background: ${({isError}) => (isError ? D.errorBg : 'linear-gradient(135deg, #392664 0%, #6E0FEB 100%)')};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-top: 2px;
`;

const AvatarError = styled.span`
	font-size: 12px;
`;

const BubbleWrapper = styled(FlexColumn)`
	flex: 1;
	min-width: 0;
	position: relative;
`;

const Bubble = styled.div<{isUser: boolean; isError: boolean}>`
	padding: 9px 12px;
	border-radius: ${({isUser}) => (isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px')};
	background: ${({isUser, isError}) => (isError ? D.errorBg : isUser ? D.userBubble : D.assistantBubble)};
	border: 1px solid
		${({isUser, isError}) => (isError ? D.errorBorder : isUser ? D.userBubbleBorder : D.assistantBubbleBorder)};
	font-size: 13px;
	line-height: 1.55;
	color: ${({isError}) => (isError ? D.errorText : D.textPrimary)};
	word-break: break-word;
	position: relative;
`;

const CopyButton = styled.button<{copied: boolean}>`
	position: absolute;
	top: 5px;
	right: 5px;
	background: ${({copied}) => (copied ? D.stepDoneBg : D.bg)};
	border: 1px solid ${({copied}) => (copied ? D.stepDoneBorder : D.border)};
	color: ${({copied}) => (copied ? D.stepDoneText : D.textMuted)};
	border-radius: 4px;
	padding: 2px 7px;
	font-size: 10px;
	cursor: pointer;
	font-weight: 500;
`;

const UserText = styled.span`
	color: ${D.accent};
	font-weight: 500;
`;

const ShowMoreRow = styled.div`
	display: flex;
	margin-top: 10px;
	gap: 8px;
	align-items: center;
	justify-content: center;
`;

const ShowLessRow = styled.div`
	display: flex;
	margin-top: 8px;
	justify-content: center;
`;

const CharCount = styled.span`
	color: ${D.textMuted};
	font-size: 11px;
`;

const Cursor = styled.span`
	display: inline-block;
	width: 2px;
	height: 12px;
	background: ${D.accentMid};
	margin-left: 2px;
	vertical-align: text-bottom;
	animation: ai-blink 1s step-end infinite;
`;

const StoppedWrapper = styled.span`
	color: ${D.textMuted};
	font-size: 12px;
	font-style: italic;
`;

const StoppedNote = styled.span`
	display: block;
	margin-top: 4px;
	font-size: 11px;
	color: ${D.textMuted};
`;

const Timestamp = styled.div<{isUser: boolean}>`
	font-size: 10px;
	color: ${D.textMuted};
	margin-top: 3px;
	text-align: ${({isUser}) => (isUser ? 'right' : 'left')};
	padding: ${({isUser}) => (isUser ? '0 2px 0 0' : '0 0 0 2px')};
`;

// ── Markdown component overrides ──────────────────────────────────────────────

type HistoryProp = {push: (path: string) => void} | null | undefined;

function buildMdComponents(history: HistoryProp) {
	return {
		p({children}: {children: React.ReactNode}) {
			return <MdP>{children}</MdP>;
		},
		a({href, children}: {href?: string; children: React.ReactNode}) {
			if (href && href.startsWith('/')) {
				return (
					<MdInternalLinkBtn type="button" onClick={() => history && history.push(href as string)}>
						{children}
					</MdInternalLinkBtn>
				);
			}
			return (
				<MdExternalLink href={href} target="_blank" rel="noopener noreferrer">
					{children}
				</MdExternalLink>
			);
		},
		code({inline, children}: {inline?: boolean; children: React.ReactNode}) {
			if (inline) {
				return <MdInlineCode>{children}</MdInlineCode>;
			}
			return (
				<MdPre>
					<MdPreCode>{children}</MdPreCode>
				</MdPre>
			);
		},
		ul({children}: {children: React.ReactNode}) {
			return <MdUl>{children}</MdUl>;
		},
		ol({children}: {children: React.ReactNode}) {
			return <MdOl>{children}</MdOl>;
		},
		li({children}: {children: React.ReactNode}) {
			return <MdLi>{children}</MdLi>;
		},
		h1({children}: {children: React.ReactNode}) {
			return <MdH1>{children}</MdH1>;
		},
		h2({children}: {children: React.ReactNode}) {
			return <MdH2>{children}</MdH2>;
		},
		h3({children}: {children: React.ReactNode}) {
			return <MdH3>{children}</MdH3>;
		},
		table({children}: {children: React.ReactNode}) {
			return (
				<MdTableWrapper>
					<MdTable>{children}</MdTable>
				</MdTableWrapper>
			);
		},
		th({children}: {children: React.ReactNode}) {
			return <MdTh>{children}</MdTh>;
		},
		td({children}: {children: React.ReactNode}) {
			return <MdTd>{children}</MdTd>;
		},
		blockquote({children}: {children: React.ReactNode}) {
			return <MdBlockquote>{children}</MdBlockquote>;
		},
	};
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Cuts at the last newline before maxChars to avoid splitting mid-table-row,
// which causes remark-gfm to emit orphaned text fragments below the table.
function safeSlice(content: string, maxChars: number): string {
	if (content.length <= maxChars) return content;
	const lastNewline = content.lastIndexOf('\n', maxChars);
	return lastNewline > maxChars / 2 ? content.slice(0, lastNewline) : content.slice(0, maxChars);
}

function formatTime(iso: string): string {
	try {
		const d = new Date(iso);
		return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
	} catch (_) {
		return '';
	}
}

// ── Component ─────────────────────────────────────────────────────────────────

type MessageBubbleProps = {
	message: Message;
	isLastAssistant: boolean;
	history: HistoryProp;
};

export function MessageBubble({message, isLastAssistant, history}: MessageBubbleProps): JSX.Element {
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const [hovered, setHovered] = useState(false);
	const {formatMessage} = useIntl();

	const mdComponents = buildMdComponents(history);

	const isUser = message.role === 'user';
	const isStopped = message.error === 'stopped';
	const isError = !!message.error && !isStopped;
	const isLong = !message.pending && !isError && message.content.length > TRUNCATE_AT;
	const displayContent = isLong && !expanded ? safeSlice(message.content, PREVIEW_CHARS) : message.content;

	function handleCopy() {
		navigator.clipboard.writeText(message.content).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	}

	return (
		<MessageRow isUser={isUser}>
			{!isUser && (
				<Avatar isError={isError}>
					{isError ? <AvatarError>⚠</AvatarError> : <SparkleIcon size={14} color="#ffffff" />}
				</Avatar>
			)}

			<BubbleWrapper onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
				<Bubble isUser={isUser} isError={isError}>
					{!isUser && !message.pending && message.content && hovered && (
						<CopyButton type="button" copied={copied} onClick={handleCopy}>
							{copied
								? formatMessage({id: 'forecast_agent.message.copied'})
								: formatMessage({id: 'forecast_agent.message.copy'})}
						</CopyButton>
					)}

					{isUser && <UserText>{message.content}</UserText>}

					{!isUser && !isError && !isStopped && (
						<>
							{message.content ? (
								<ReactMarkdown
									remarkPlugins={[remarkGfm]}
									components={mdComponents as Parameters<typeof ReactMarkdown>[0]['components']}
								>
									{displayContent}
								</ReactMarkdown>
							) : null}
							{isLong && !expanded && (
								<ShowMoreRow>
									<CharCount>
										+{(message.content.length - displayContent.length).toLocaleString()} chars
									</CharCount>
									<SmallBtn onClick={() => setExpanded(true)}>
										<FormattedMessage id="forecast_agent.message.show_more" />
									</SmallBtn>
								</ShowMoreRow>
							)}
							{isLong && expanded && (
								<ShowLessRow>
									<SmallBtn onClick={() => setExpanded(false)}>
										<FormattedMessage id="forecast_agent.message.show_less" />
									</SmallBtn>
								</ShowLessRow>
							)}
							{message.pending && message.content.length === 0 && <ThinkingDots />}
							{message.pending && message.content.length > 0 && <Cursor />}
						</>
					)}

					{isStopped && (
						<StoppedWrapper>
							{message.content ? (
								<>
									<ReactMarkdown
										remarkPlugins={[remarkGfm]}
										components={mdComponents as Parameters<typeof ReactMarkdown>[0]['components']}
									>
										{message.content}
									</ReactMarkdown>
									<StoppedNote>
										<FormattedMessage id="forecast_agent.message.stopped" />
									</StoppedNote>
								</>
							) : (
								<FormattedMessage id="forecast_agent.message.stopped" />
							)}
						</StoppedWrapper>
					)}

					{isError && <span>{message.error}</span>}
				</Bubble>

				{message.createdAt && <Timestamp isUser={isUser}>{formatTime(message.createdAt)}</Timestamp>}

				{!isUser && message.statusSteps && message.statusSteps.length > 0 && (
					<StatusSteps steps={message.statusSteps} isActive={isLastAssistant} />
				)}
			</BubbleWrapper>
		</MessageRow>
	);
}
